import { useEffect, useRef } from 'react';
import Plyr from 'plyr';
import 'plyr/dist/plyr.css';

import { useRefValue } from '../../hooks';

export type PlurPlayerProps = {
  videoId: string | null;

  onPlay?: () => void;
  onStop?: () => void;
};

export const usePlurPlayer = ({ videoId, onPlay, onStop }: PlurPlayerProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const playerInstanceRef = useRef<Plyr | null>(null);

  const onPlayRef = useRefValue(onPlay);
  const onStopRef = useRefValue(onStop);

  useEffect(
    () => {
      if (!videoId || !containerRef.current) return;

      containerRef.current.innerHTML = '';

      const playerDiv = document.createElement('div');
      playerDiv.setAttribute('data-plyr-provider', 'youtube');
      playerDiv.setAttribute('data-plyr-embed-id', videoId);
      containerRef.current.appendChild(playerDiv);

      const plyr = new Plyr(playerDiv, {
        youtube: {
          rel: 0,
          noCookie: true,
          modestbranding: 1,
        },
        fullscreen: { enabled: true },
        controls: [
          'play',
          'progress',
          'current-time',
          'mute',
          'volume',
          'fullscreen',
        ],
      });

      plyr.on('play', () => {
        onPlayRef.current?.();
      });
      plyr.on('pause', () => {
        onStopRef.current?.();
      });

      playerInstanceRef.current = plyr;

      return () => {
        plyr.destroy();
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [videoId, onPlayRef, onStopRef]
  );

  // Prevent context menu and disable pointer events inside iframe
  useEffect(
    () => {
      const blockContext = (e: MouseEvent) => e.preventDefault();
      const container = containerRef.current;
      if (container) {
        container.addEventListener('contextmenu', blockContext);
      }
      return () => {
        container?.removeEventListener('contextmenu', blockContext);
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return { containerRef, playerInstanceRef };
};
